// The Data tab, readable first (DES1 DRAFTS-AND-READY §Data tab; ARCH: presentation only, over the rows the existing
// authorized read already returned). Nothing here is read, guessed or invented: counts are the rows returned, a "latest"
// time is a time stored in the rows (never the read time), and the display fields are worked out from COLUMN TYPES and
// the returned values, never from field-name word lists. Labels are text; values are never executed or rendered as HTML.
import type { DataColumn, DataRow } from './data-model';

/** A short text is a candidate for the list line; longer text only appears in the expanded entry. */
export const SHORT_TEXT = 80;
/** A text field with this few distinct values is a category ("Boozt", "NA-KD"), good for the summary. */
export const FEW_VALUES = 4;

export type DisplayFields = {
  time: string | null;
  tag: string | null;
  count: string | null;
  other: string | null;
};

const present = (value: unknown): value is string | number | boolean =>
  value !== null && value !== undefined && value !== '';

const textOf = (row: DataRow, key: string) => {
  const value = row.cells[key];
  return typeof value === 'string' && value !== '' ? value : null;
};

/** The parsed JSON of a cell, or undefined when it is not JSON text. */
export function parseJson(value: unknown): unknown {
  if (typeof value !== 'string') return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

/**
 * Pick the few fields that tell entries apart: the first time field, a low-variety short text (the category), the
 * largest array (a count) and one other short text. Absent kinds are null.
 */
export function pickDisplay(
  columns: readonly DataColumn[],
  rows: readonly DataRow[],
): DisplayFields {
  const typed = columns.filter((column) => column.type);
  const time =
    typed.find(
      (column) =>
        (column.type === 'timestamp' || column.type === 'date') &&
        rows.some((row) => textOf(row, column.key)),
    )?.key ?? null;
  const shortText = typed.filter(
    (column) =>
      column.type === 'text' &&
      column.key !== time &&
      rows.some((row) => textOf(row, column.key)) &&
      rows.every((row) => (textOf(row, column.key) ?? '').length <= SHORT_TEXT),
  );
  const distinct = (key: string) =>
    new Set(rows.map((row) => textOf(row, key)).filter(Boolean)).size;
  const tag = shortText.find((column) => distinct(column.key) <= FEW_VALUES)?.key ?? null;
  let count: string | null = null;
  let largest = 0;
  for (const column of typed.filter((c) => c.type === 'json')) {
    const longest = Math.max(
      0,
      ...rows.map((row) => {
        const parsed = parseJson(row.cells[column.key]);
        return Array.isArray(parsed) ? parsed.length : 0;
      }),
    );
    if (longest > largest) {
      largest = longest;
      count = column.key;
    }
  }
  const other = shortText.find((column) => column.key !== tag)?.key ?? null;
  return { time, tag, count, other };
}

/** Newest first when there is a time field; otherwise the order the read returned. */
export function orderEntries(rows: readonly DataRow[], time: string | null): DataRow[] {
  if (!time) return [...rows];
  const at = (row: DataRow) => {
    const value = textOf(row, time);
    const ms = value ? Date.parse(value) : NaN;
    return Number.isFinite(ms) ? ms : -Infinity;
  };
  return [...rows].sort((a, b) => at(b) - at(a));
}

/** The latest stored time among the returned rows (null when there is no time field or no value). */
export function latestTime(rows: readonly DataRow[], time: string | null): string | null {
  if (!time) return null;
  let best: { ms: number; value: string } | null = null;
  for (const row of rows) {
    const value = textOf(row, time);
    const ms = value ? Date.parse(value) : NaN;
    if (value && Number.isFinite(ms) && (!best || ms > best.ms)) best = { ms, value };
  }
  return best?.value ?? null;
}

/** The distinct values of the category field, in first-seen order (the summary names them). */
export function tagValues(rows: readonly DataRow[], tag: string | null): string[] {
  if (!tag) return [];
  return [...new Set(rows.map((row) => textOf(row, tag)).filter((v): v is string => Boolean(v)))];
}

/** The first short string inside the first element of an array (its "top" entry), bounded. */
export function firstEntryName(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const first = value[0];
  if (typeof first === 'string') return first.slice(0, SHORT_TEXT);
  if (first && typeof first === 'object')
    for (const inner of Object.values(first as Record<string, unknown>))
      if (typeof inner === 'string' && inner && inner.length <= SHORT_TEXT) return inner;
  return null;
}

/** A display path such as `items.length` or `items[0].name`: bounded own-property selectors, never an expression. */
export function selectPath(value: unknown, path: string): unknown {
  if (!/^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*|\[\d{1,3}\])*(\.length)?$/u.test(path)) return undefined;
  let current: unknown = value;
  for (const part of path.match(/[A-Za-z_]\w*|\[\d+\]/gu) ?? []) {
    if (current === null || current === undefined) return undefined;
    if (part.startsWith('[')) {
      if (!Array.isArray(current)) return undefined;
      current = current[Number(part.slice(1, -1))];
    } else if (part === 'length' && Array.isArray(current)) {
      current = current.length;
    } else if (typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, part)) {
      current = (current as Record<string, unknown>)[part];
    } else return undefined;
  }
  return current;
}

/** The row as its authorized projection: column id -> value (JSON cells parsed). Show raw and Copy use exactly this. */
export function rawRow(columns: readonly DataColumn[], row: DataRow): Record<string, unknown> {
  return Object.fromEntries(
    columns.map((column) => {
      const value = row.cells[column.key] ?? null;
      const parsed = column.type === 'json' ? parseJson(value) : undefined;
      return [column.key, parsed !== undefined ? parsed : value];
    }),
  );
}
